/**
 * Tiny module-level toast store for the God Console — operator actions confirm
 * success/failure so a swallowed error can't read as success. Framework-agnostic
 * pub/sub; <Toaster/> (App shell) subscribes via useToasts().
 */
import { useEffect, useState } from "react";
import { ApiError } from "./api";

export type ToastKind = "success" | "error" | "info";
export interface Toast {
  id: number;
  kind: ToastKind;
  msg: string;
}

let seq = 0;
let current: Toast[] = [];
const listeners = new Set<(t: Toast[]) => void>();
const emit = () => listeners.forEach((l) => l(current));

export function dismissToast(id: number): void {
  current = current.filter((t) => t.id !== id);
  emit();
}

export function pushToast(kind: ToastKind, msg: string): void {
  const id = ++seq;
  current = [...current.slice(-3), { id, kind, msg }]; // cap at ~4 on screen
  emit();
  // Errors linger longer (the operator needs to read what went wrong).
  setTimeout(() => dismissToast(id), kind === "error" ? 6500 : 3500);
}

export function useToasts(): Toast[] {
  const [t, setT] = useState<Toast[]>(current);
  useEffect(() => {
    listeners.add(setT);
    setT(current);
    return () => { listeners.delete(setT); };
  }, []);
  return t;
}

/** Pulls a human message out of a thrown ApiError ({error:"..."} body or status). */
export function errorDetail(e: unknown): string {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body);
      if (parsed && typeof parsed.error === "string") return parsed.error;
    } catch { /* body isn't JSON */ }
    return e.body && e.body.length < 120 ? e.body : `HTTP ${e.status}`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}
